/**
 * Two-Factor Authentication Store
 *
 * Manages 2FA verification state during API requests.
 *
 * IMPORTANT: This store does NOT use:
 * - devtools middleware: Not needed for ephemeral 2FA state
 * - persist middleware: 2FA state should NEVER be persisted to storage
 *   (session IDs, pending requests, and verification codes are sensitive
 *    and should only exist in memory during active verification)
 *
 * State is cleared when:
 * - Verification completes (success or error)
 * - User cancels the modal
 * - Page refresh (state is lost, which is intentional)
 */

import { useMemo } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { ApiResult } from '@/services/apiTypes';
import { TwoFactorMethod } from '@/types/misc';

type PendingRequest = {
  execute: (code: string) => Promise<ApiResult<unknown>>;
};

type TwoFAState = {
  isRequired: boolean;
  sessionId?: string;
  method?: TwoFactorMethod;
  pendingRequest?: PendingRequest;
  error?: string;
  apiResult?: ApiResult<unknown>; // Store the API result when pending request completes
  isVerificationSuccessful?: boolean; // Track if verification succeeded

  requireTwoFA: (payload: { sessionId: string; method: TwoFactorMethod; pendingRequest: PendingRequest }) => void;

  clear: () => void;
  clearPendingRequest: () => void; // Clear only pendingRequest, keep apiResult for middleware to pick up
  setError: (error: string | undefined) => void;
  setVerificationResult: (apiResult: ApiResult<unknown>) => void;
};

export const useTwoFactorStore = create<TwoFAState>()(
  devtools(
    (set) => ({
      isRequired: false,
      sessionId: undefined,
      method: undefined,
      pendingRequest: undefined,
      error: undefined,
      apiResult: undefined,
      isVerificationSuccessful: undefined,

      requireTwoFA: (payload) => {
        set({
          isRequired: true,
          sessionId: payload.sessionId,
          method: payload.method,
          pendingRequest: payload.pendingRequest,
          error: undefined,
          apiResult: undefined,
          isVerificationSuccessful: undefined,
        });
      },

      clear: () => {
        set({
          isRequired: false,
          sessionId: undefined,
          method: undefined,
          pendingRequest: undefined,
          error: undefined,
          apiResult: undefined,
          isVerificationSuccessful: undefined,
        });
      },

      clearPendingRequest: () => {
        set({
          pendingRequest: undefined,
        });
      },

      setError: (error) => {
        set({ error });
      },

      setVerificationResult: (apiResult) => {
        const isSuccess = apiResult.error === null;
        set({
          apiResult,
          isVerificationSuccessful: isSuccess,
        });
      },
    }),
    { name: 'TwoFactorStore' }
  )
);

// ============================================================================
// Selectors for Components
// ============================================================================

/**
 * Selector for modal component
 * Returns modal state needed for display
 *
 * Uses individual selectors and memoizes the result to prevent infinite re-renders.
 * Components can destructure directly like: const { isOpen, method } = useTwoFactorModal()
 */
export const useTwoFactorModal = () => {
  const isRequired = useTwoFactorStore((state) => state.isRequired);
  const sessionId = useTwoFactorStore((state) => state.sessionId);
  const method = useTwoFactorStore((state) => state.method);
  const error = useTwoFactorStore((state) => state.error);
  const clear = useTwoFactorStore((state) => state.clear);

  return useMemo(
    () => ({
      isOpen: isRequired,
      sessionId,
      method,
      error,
      clear,
    }),
    [isRequired, sessionId, method, error, clear]
  );
};

/**
 * Selector for modal form data
 * Returns method, error, and pendingRequest for form handling
 *
 * Uses individual selectors and memoizes the result to prevent infinite re-renders.
 */
export const useTwoFactorForm = () => {
  const method = useTwoFactorStore((state) => state.method);
  const error = useTwoFactorStore((state) => state.error);
  const pendingRequest = useTwoFactorStore((state) => state.pendingRequest);

  return useMemo(
    () => ({
      method,
      error,
      pendingRequest,
    }),
    [method, error, pendingRequest]
  );
};

/**
 * Selector for modal visibility
 * Returns only the isRequired flag
 */
export const useTwoFactorIsRequired = () => useTwoFactorStore((state) => state.isRequired);

/**
 * Selector for 2FA actions
 * Returns all action functions for updating state
 *
 * Action functions are stable references from Zustand, but we memoize the object
 * to ensure consistent reference equality.
 */
export const useTwoFactorActions = () => {
  const requireTwoFA = useTwoFactorStore((state) => state.requireTwoFA);
  const clear = useTwoFactorStore((state) => state.clear);
  const setError = useTwoFactorStore((state) => state.setError);
  const setVerificationResult = useTwoFactorStore((state) => state.setVerificationResult);

  return useMemo(
    () => ({
      requireTwoFA,
      clear,
      setError,
      setVerificationResult,
    }),
    [requireTwoFA, clear, setError, setVerificationResult]
  );
};

/**
 * Selector for getting actions without hook (for use outside components)
 * Use this in event handlers, callbacks, or non-React code
 */
export const getTwoFactorActions = () => {
  const store = useTwoFactorStore.getState();
  return {
    requireTwoFA: store.requireTwoFA,
    clear: store.clear,
    clearPendingRequest: store.clearPendingRequest,
    setError: store.setError,
    setVerificationResult: store.setVerificationResult,
  };
};
